/* eslint-disable react/prop-types */
import { classNames, React, useEffect, useState } from '@digsup/core-ui';
import PropTypes from 'prop-types';
import { TooltipTrigger } from './ToolTip';
import './Popover.scss';

export const Popover = ({
  children,
  renderer,
  placement,
  trigger,
  width,
  theme,
  zIndex,
  tooltipShown,
  tabIndex,
  portalContainer,
  referenceOffset,
  ...rest
}) => {
  const [id] = useState(`tooltip-${Math.random().toString(36).substr(2, 5)}`);

  const [visible, onVisibleChange] = useState(tooltipShown);

  useEffect(() => {
    const handleKeyDown = ({ key }) => {
      if (key === 'Escape') {
        onVisibleChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => onVisibleChange(tooltipShown), [tooltipShown]);

  return (
    <TooltipTrigger
      trigger={trigger}
      tooltipShown={visible}
      onVisibilityChange={onVisibleChange}
      placement={placement}
      closeOnReferenceHidden={false}
      portalContainer={portalContainer}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [-12, referenceOffset],
          },
        },
        {
          name: 'arrow',
          options: {
            padding: 5, // 5px from the edges of the popper
          },
        },
      ]}
      tooltip={({ tooltipRef, getArrowProps, getTooltipProps }) => (
        <div
          {...getTooltipProps({
            id,
            ref: tooltipRef,
            'data-placement': placement,
            className: classNames(`c-popover tooltip-container`, {
              [`t-${theme}`]: !!theme,
              [`c-popover--width-${width}`]: width,
              [`c-popover--z-index-${zIndex}`]: !!zIndex,
            }),
          })}>
          <div
            {...getArrowProps({
              'data-placement': placement,
              className: 'tooltip-arrow c-popover-arrow',
            })}
          />
          {renderer({ ...rest, onVisibleChange })}
        </div>
      )}>
      {({ triggerRef }) => (
        <span
          ref={triggerRef}
          tabIndex={tabIndex}
          className={`c-popover__trigger ${
            'aria-controls' && 'aria-describedby' && id
          }`}>
          {children}
        </span>
      )}
    </TooltipTrigger>
  );
};

Popover.propTypes = {
  /**
   * width options
   */
  width: PropTypes.oneOf(['auto', 'small', 'medium', 'large']),
  /**
   * The event(s) that trigger the popover.
   */
  trigger: PropTypes.oneOf(['none', 'click', 'hover']),
  /**
   * Popover theme
   */
  theme: PropTypes.oneOf(['none', 'danger', 'important', 'reward', 'positive']),

  children: PropTypes.any,
  /**
   * Popover placement
   */
  placement: PropTypes.oneOf([
    'auto',
    'left',
    'left-start',
    'left-end',
    'right',
    'right-start',
    'right-end',
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
  ]),
  /**
   * Popover className
   */
  className: PropTypes.string,
  tooltipShown: PropTypes.bool,
  referenceOffset: PropTypes.number,
  portalContainer: PropTypes.any,
  zIndex: PropTypes.oneOf(['page', 'modal']),
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Popover.defaultProps = {
  tabIndex: 0,
  referenceOffset: 5,
  trigger: 'click',
  theme: 'none',
  zIndex: 'page',
  placement: 'auto',
  width: 'auto',
};
